import { round } from "mathjs";
import { OfferDiscount } from "src/app/domains";

import { ArticleStatus, InternalArticleVariant } from "../features/catalog/domains/article";
import type { Article } from "../features/catalog/domains/article";

export enum OfferUnitType {
  MATERIAL = "MATERIAL",
  WORKFORCE = "WORKFORCE",
  OTHER = "OTHER",
  TEXT = "TEXT",
  PACK = "PACK",
}
export enum OfferUnitTypeLabel {
  MATERIAL = "Matériel",
  WORKFORCE = "Main d'oeuvre",
  OTHER = "Divers",
  TEXT = "Texte",
  PACK = "Pack",
}
export enum OfferUnitTypeDefaultUnit {
  MATERIAL = "pce",
  WORKFORCE = "j/e",
  OTHER = "ens",
}
export enum OfferItemType {
  NORMAL = "NORMAL",
  VARIANT = "VARIANT",
  TEXT = "TEXT",
  PERCENT = "PERCENT",
}
export enum OfferItemTypeLabel {
  NORMAL = "Normal",
  VARIANT = "Avec variantes",
  TEXT = "Texte",
  PERCENT = "Plus-value",
}

/**
 * Une ligne de chiffrage entièrement neutralisée : uniquement une position de MO pure
 * dont la MO a été coupée. Un pack dont la MO est coupée reste compté — seul son prix baisse.
 * Prend un objet brut autant qu'une instance : les utilitaires PVPR reçoivent parfois
 * des données désérialisées sans getters.
 */
export function isWorkforceLineDisabled(item: {
  unit_type: OfferUnitType | string;
  is_workforce_disabled?: boolean;
}): boolean {
  return item?.unit_type === OfferUnitType.WORKFORCE && item?.is_workforce_disabled === true;
}

export class OfferItemVariant {
  static LockedFieldEnum = {
    MARGIN: "MARGIN",
    SELLING: "SELLING",
    COST: "COST",
  } as const;

  offer_item_variant_id = generateObjectId();
  name = "";
  unit = "pce";
  quantity = 1;
  cost_unit_price = 0;
  selling_unit_price = 0;

  // Not in DB

  locked_field: keyof typeof OfferItemVariant.LockedFieldEnum = OfferItemVariant.LockedFieldEnum.MARGIN;
  margin = 25;

  get selling_price(): number {
    return this.selling_unit_price * this.quantity;
  }
  get cost_price(): number {
    return this.cost_unit_price * this.quantity;
  }

  public static createInstance(jsonData: OfferItemVariant): OfferItemVariant {
    const d = Object.assign(new OfferItemVariant(), jsonData);
    d.margin = d.calculateMargin();
    return d;
  }

  public static createInstances(jsonData: any[]): OfferItemVariant[] {
    if (!jsonData) return [];
    return jsonData.map(OfferItemVariant.createInstance);
  }

  private calculateMargin(): number {
    return this.cost_unit_price ? roundFixed((this.selling_unit_price / this.cost_unit_price - 1) * 100, 2) : 0;
  }

  public onCostUnitPriceChange(offerItem: OfferItem = null) {
    if (this.locked_field !== OfferItemVariant.LockedFieldEnum.MARGIN) {
      this.margin = this.calculateMargin();
    } else {
      this.selling_unit_price = roundFixed(this.cost_unit_price * (1 + this.margin / 100), 2);
    }
    if (offerItem) {
      let cumulVariantTotalCostPrice: number = 0;
      let cumulVariantTotalSellingPrice: number = 0;

      for (let variant of offerItem.variants) {
        cumulVariantTotalCostPrice += variant.cost_price;
        cumulVariantTotalSellingPrice += variant.selling_price;
      }

      offerItem.setCostPrice(cumulVariantTotalCostPrice);
      offerItem.setSellingPrice(cumulVariantTotalSellingPrice);
    }
  }

  public onSellingUnitPriceChange(offerItem: OfferItem = null) {
    if (this.locked_field !== OfferItemVariant.LockedFieldEnum.MARGIN) {
      this.margin = this.calculateMargin();
    } else {
      this.cost_unit_price = roundFixed(this.selling_unit_price / (1 + this.margin / 100), 2);
    }
    if (offerItem) {
      let cumulVariantTotalCostPrice: number = 0;
      let cumulVariantTotalSellingPrice: number = 0;

      for (let variant of offerItem.variants) {
        cumulVariantTotalCostPrice += variant.cost_price;
        cumulVariantTotalSellingPrice += variant.selling_price;
      }

      offerItem.setCostPrice(cumulVariantTotalCostPrice);
      offerItem.setSellingPrice(cumulVariantTotalSellingPrice);
    }
  }

  public onMarginChange(offerItem: OfferItem = null) {
    if (this.locked_field !== OfferItemVariant.LockedFieldEnum.SELLING) {
      this.selling_unit_price = roundFixed(this.cost_unit_price * (1 + this.margin / 100), 2);
    } else {
      this.cost_unit_price = roundFixed(this.selling_unit_price / (1 + this.margin / 100), 2);
    }

    if (offerItem) {
      let cumulVariantTotalCostPrice: number = 0;
      let cumulVariantTotalSellingPrice: number = 0;

      for (let variant of offerItem.variants) {
        cumulVariantTotalCostPrice += variant.cost_price;
        cumulVariantTotalSellingPrice += variant.selling_price;
      }

      offerItem.setCostPrice(cumulVariantTotalCostPrice);
      offerItem.setSellingPrice(cumulVariantTotalSellingPrice);
    }
  }
}

export class OfferItemArticle {
  article_id = ""; //NOT saved in DB
  catalog_id = "";
  supplier_id = "";
  vat = 0;
  unit = "";
  article_type: OfferUnitType;
  status: ArticleStatus;
  long_text = "";
  short_text = "";
  article_number = "pce";
  cost_price = 0;
  selling_price = 0;
  margin = 0;
  quantity = 1;
  articleMarginPercentage: number;
  supplier_discount_goal: number;

  isCostPriceLocked: boolean = false;
  isSellingPriceLocked: boolean = false;
  service_nature_id: string = "";
  is_workforce_disabled: boolean = false;

  constructor(supplier_discount_goal = 25) {
    this.supplier_discount_goal = supplier_discount_goal;
    this.offer_discounts = [];
  }

  setSupplierDiscountGoal(supplier_discount_goal: number) {
    this.supplier_discount_goal = supplier_discount_goal;
    this.computeSupplierDiscountGoalTotal();
    this.computeSupplierDiscountGoalRate();
    this.computeBuyPriceGoal();
  }
  public supplier_discount_goal_total: number = 0;
  computeSupplierDiscountGoalTotal() {
    this.supplier_discount_goal_total = (this.selling_price_with_auction * this.supplier_discount_goal) / 100;
  }

  public buy_price_goal: number = 0;
  computeBuyPriceGoal() {
    this.buy_price_goal = this.selling_price_with_auction - this.supplier_discount_goal_total || 0;
  }

  public supplier_discount_goal_rate: number = 0;
  computeSupplierDiscountGoalRate() {
    this.supplier_discount_goal_rate =
      Math.abs((this.selling_price_with_auction / (this.cost_price * this.quantity)) * 100 - 100) || 0;
  }

  public selling_price_with_auction: number = 0;
  computeSellingPriceWithAuction() {
    let newPrice = this.selling_price;
    if (this.offer_discounts) {
      for (const offer_discount of this.offer_discounts) {
        let discount_total = (newPrice * offer_discount.discount) / 100;
        newPrice = newPrice - discount_total;
      }
    }
    this.selling_price_with_auction = newPrice;
  }
  offer_discounts: OfferDiscount[];

  public static createInstance(jsonData: OfferItemArticle): OfferItemArticle {
    const d = Object.assign(new OfferItemArticle(), jsonData);
    // Initialisation de articleMarginPercentage en fonction de la valeur de margin
    d.articleMarginPercentage = d.margin * 100;
    return d;
  }

  public static createInstances(jsonData: any[]): OfferItemArticle[] {
    if (!jsonData) return [];
    return jsonData.map(OfferItemArticle.createInstance);
  }

  /**
   * Construit un OfferItemArticle (sous-ligne de pack) depuis un sous-article catalogue.
   * Partagé entre le chiffrage d'offre et la facture libre pour ne pas dupliquer la règle
   * de taux AMFIS (MO : coût = taux d'achat, vente = taux de vente, fallback taux guide).
   */
  public static fromCatalogArticle(
    a: Article,
    amfis: { isAmfisCatalog?: boolean; amfisRate?: number | null; amfisCost?: number | null } = {},
  ): OfferItemArticle {
    const { isAmfisCatalog = false, amfisRate = null, amfisCost = null } = amfis;
    const tmp = new OfferItemArticle();
    tmp.article_number = a.article_number;
    tmp.article_type = a.article_type;
    tmp.catalog_id = a.catalog_id;
    tmp.vat = a.vat;
    tmp.unit = a.unit;
    tmp.status = a.status;
    tmp.supplier_id = a.supplier_id;
    tmp.long_text = a.long_text;
    tmp.short_text = a.short_text;

    let subCost: number;
    let subMargin: number;
    if (isAmfisCatalog && a.article_type === OfferUnitType.WORKFORCE) {
      const sellRate = amfisRate ?? a.price;
      const costRate = amfisCost ?? amfisRate ?? a.price;
      subCost = costRate;
      subMargin = costRate > 0 ? sellRate / costRate - 1 : 0;
    } else {
      subCost = a.price;
      subMargin = a.sell_margin;
    }
    tmp.cost_price = subCost;
    tmp.margin = subMargin;
    tmp.articleMarginPercentage = subMargin * 100;
    tmp.article_id = a.article_id;
    tmp.quantity = a.quantity;
    tmp.selling_price = subCost * a.quantity * (1 + subMargin);
    tmp.isCostPriceLocked = !!a.is_buy_price_locked;
    tmp.isSellingPriceLocked = !!a.is_sell_price_locked;
    tmp.service_nature_id =
      a.article_type === OfferUnitType.TEXT || a.article_type === OfferUnitType.PACK ? "" : a.service_nature_id || "";

    return tmp;
  }

  /** Bascule PR/PV/marge/quantité d'une sous-ligne de pack selon le champ modifié et les verrous. */
  public static applyFieldChange(
    article: OfferItemArticle,
    fromInput: "COST_PRICE" | "SELLING_PRICE" | "MARGIN" | "QUANTITY",
  ): void {
    const costLocked = !!article.isCostPriceLocked;
    const sellingLocked = !!article.isSellingPriceLocked;
    const qty = article.quantity || 0;

    switch (fromInput) {
      case "COST_PRICE":
        article.cost_price = article.cost_price || 0;
        if (sellingLocked) {
          if (article.cost_price > 0 && qty > 0) {
            article.margin = article.selling_price / (article.cost_price * qty) - 1;
          } else {
            article.margin = 0;
          }
          article.articleMarginPercentage = article.margin * 100;
        } else {
          article.selling_price = article.cost_price * (1 + article.margin) * qty;
        }
        break;

      case "SELLING_PRICE":
        article.selling_price = article.selling_price || 0;
        if (costLocked) {
          if (article.cost_price > 0 && qty > 0) {
            article.margin = article.selling_price / (article.cost_price * qty) - 1;
          } else {
            article.margin = 0;
          }
          article.articleMarginPercentage = article.margin * 100;
        } else {
          const effectiveQty = qty || 1;
          if (1 + article.margin !== 0) {
            article.cost_price = article.selling_price / ((1 + article.margin) * effectiveQty);
          }
        }
        break;

      case "MARGIN":
        article.margin = (article.articleMarginPercentage || 0) / 100;
        if (sellingLocked) {
          if (qty > 0 && 1 + article.margin !== 0) {
            article.cost_price = article.selling_price / ((1 + article.margin) * qty);
          }
        } else {
          article.selling_price = article.cost_price * (1 + article.margin) * qty;
        }
        break;

      case "QUANTITY":
        article.quantity = qty;
        // Le lock PV est un lock UNITAIRE (cohérent catalogue) : on ne touche ni cost_price ni margin,
        // on recalcule juste le PV total = cost_price * (1+margin) * qty.
        article.selling_price = article.cost_price * (1 + article.margin) * qty;
        break;
    }
  }

  /** Édition du PV UNITAIRE d'une sous-ligne de pack : ajuste cost_price ou margin selon les locks. */
  public static applyUnitSellingPriceChange(article: OfferItemArticle, newUnitPrice: number): void {
    const unit = Number(newUnitPrice) || 0;
    const qty = article.quantity || 0;
    const costLocked = !!article.isCostPriceLocked;

    if (costLocked) {
      if (article.cost_price > 0) {
        article.margin = unit / article.cost_price - 1;
        article.articleMarginPercentage = article.margin * 100;
      }
    } else {
      if (1 + article.margin !== 0) {
        article.cost_price = unit / (1 + article.margin);
      }
    }

    article.selling_price = unit * qty;
  }

  toggleCostPriceLock(): void {
    if (!this.cost_price || this.cost_price === 0) {
      this.isCostPriceLocked = false;
      return;
    }
    this.isCostPriceLocked = !this.isCostPriceLocked;
  }

  toggleSellingPriceLock(): void {
    if (!this.selling_price || this.selling_price === 0) {
      this.isSellingPriceLocked = false;
      return;
    }
    this.isSellingPriceLocked = !this.isSellingPriceLocked;
  }
}

export interface IOfferItem {
  offer_item_id: string;
  article_id: string;
  article_number: string;
  isPageBreakBefore: boolean;
  isCostPriceLocked: boolean;
  isSellingPriceLocked: boolean;
  isInvalid: boolean;
  name: string;
  offer_item_type: OfferItemType;
  supplier_id: string;
  unit_quantity: number;
  percentage: number;
  unit_type: string;
  unit: string;
  unit_price: number;
  margin: number;
  auction_discount: number;
  supplier_discount_goal: number;
  billing_percent_situations: number[];

  desc_html: string;
  is_ttc: boolean;
  taxes_rate_percent: number;
  apply_discount: boolean;
  discount_value: number;

  progress: number;
  employees_ids: [];

  is_optional: boolean;
  is_workforce_disabled: boolean;
  service_nature_id: string;
  // NOT REGISTER IN DB
  is_selected: boolean;

  variants: OfferItemVariant[];

  articles: OfferItemArticle[];
}

export class OfferItem implements IOfferItem {
  offer_item_id: string;
  name: string;
  offer_item_type: OfferItemType;
  desc_html: string;
  supplier_id: string;
  unit_type: OfferUnitType;
  unit: string;
  article_id: string;
  article_number: string;
  isPageBreakBefore: boolean = false;
  isCostPriceLocked: boolean = false;
  isSellingPriceLocked: boolean = false;
  percentage: number = 0;
  isInvalid: boolean = false;
  // NOT REGISTER IN DB
  is_selected: boolean = false;
  color: string;

  is_ttc: boolean = false;
  taxes_rate_percent: number;
  get taxes_rate(): number {
    return this.taxes_rate_percent / 100 + 1;
  }

  unit_quantity: number;
  setUnitQuantity(unit_quantity: number) {
    this.unit_quantity = unit_quantity;
    this.computeValues();
  }

  unit_price: number;
  setUnitPrice(unit_price: number) {
    this.unit_price = unit_price;
    this.computeValues();
  }

  margin: number;
  setMargin(margin: number, recalculate: boolean = true, applyToSellingPrice: boolean = true) {
    if (!margin) {
      margin = 0;
    }
    this.margin = roundFixed(margin, 12);
    if (!applyToSellingPrice) {
      this.setUnitPrice(this.selling_price_unit / (1 + this.margin / 100));
    }
    if (recalculate) this.computeValues("margin");
  }

  adjustMargin(delta: number, recalculate: boolean = true, applyToSellingPrice: boolean = true) {
    const current = Number(this.margin);
    const base = Number.isFinite(current) ? current : 0;
    this.setMargin(base + (Number(delta) || 0), recalculate, applyToSellingPrice);
  }

  apply_discount: boolean = false;
  discount_percent: number;
  // Mode transitoire (non persisté, non énumérable — voir constructeur) : mémorise
  // si le dernier champ rabais touché est le montant (CHF) ou le pourcentage, pour
  // savoir si discount_value doit être re-dérivé quand le prix de la ligne change
  // de signe (moins-value).
  private _discountInputMode: "value" | "percent";
  private _lastDiscountBaseSign: number;
  setDiscountPercent(discount_percent: number) {
    this._discountInputMode = "percent";
    this.discount_percent = discount_percent;
    this.computeDiscountValue();
    this._lastDiscountBaseSign = Math.sign(this.selling_price) || null;
  }

  discount_value: number = 0;
  setDiscountValue(discount_value: number) {
    this._discountInputMode = "value";
    this.discount_value = discount_value;
    this.computeDiscountPercent();
  }

  auction_discount: number;
  supplier_discount_goal: number;
  setSupplierDiscountGoal(supplier_discount_goal: number) {
    this.supplier_discount_goal = supplier_discount_goal;
    this.computeSupplierDiscountGoalTotal();
    this.computeSupplierDiscountGoalRate();
    this.computeBuyPriceGoal();
  }
  billing_percent_situations: number[];
  progress: number;
  employees_ids: [];

  // external
  offer_discounts: OfferDiscount[];

  is_optional: boolean = false;
  service_nature_id: string = "";
  is_workforce_disabled: boolean = false;

  /** Ligne entièrement neutralisée (MO pure coupée) : exclue de tous les totaux. */
  get isWorkforceLineDisabled(): boolean {
    return isWorkforceLineDisabled(this);
  }

  /** État affiché du toggle, packs inclus. */
  get isWorkforceDisabled(): boolean {
    if (this.unit_type === OfferUnitType.PACK) {
      const workforceArticles = (this.articles ?? []).filter((a) => a.article_type === OfferUnitType.WORKFORCE);
      return workforceArticles.length > 0 && workforceArticles.every((a) => a.is_workforce_disabled);
    }
    return this.isWorkforceLineDisabled;
  }

  /** Le toggle n'a de sens que sur un pack porteur de MO ou une position de MO pure. */
  get hasWorkforceToToggle(): boolean {
    if (this.unit_type === OfferUnitType.PACK) {
      return (this.articles ?? []).some((a) => a.article_type === OfferUnitType.WORKFORCE);
    }
    return this.unit_type === OfferUnitType.WORKFORCE;
  }

  /**
   * Contribution MO de la ligne au budget main d'œuvre du chantier : le PV auction de la ligne
   * pour une position de MO pure, ou la part MO des sous-articles actifs d'un pack (remise
   * auction du pack appliquée à cette part, sous-articles MO désactivés exclus).
   *
   * `totalSellingPriceWithAuction(WORKFORCE)` ne voit pas cette part : son filtre plat sur
   * `unit_type` exclut tout pack, qui vaut toujours `unit_type === PACK`.
   */
  get workforceSellingPriceWithAuction(): number {
    if (!this.shouldCount) return 0;
    if (this.unit_type === OfferUnitType.WORKFORCE) {
      return +this.selling_price_with_auction;
    }
    if (this.unit_type === OfferUnitType.PACK) {
      return this.packWorkforceSellingPriceWithAuction();
    }
    return 0;
  }

  private packWorkforceSellingPriceWithAuction(): number {
    const unitQuantity = toNumber(this.unit_quantity) || 1;
    let total = 0;
    for (const article of this.articles ?? []) {
      if (article.article_type !== OfferUnitType.WORKFORCE) continue;
      if (article.is_workforce_disabled) continue;

      let price = toNumber(article.selling_price) * unitQuantity;
      if (this.offer_discounts) {
        for (const offer_discount of this.offer_discounts) {
          price -= (price * offer_discount.discount) / 100;
        }
      }
      total += price;
    }
    return total;
  }

  /**
   * Non destructif : rien n'est remis à zéro. Un pack voit la part de sa MO retirée de son
   * prix ; une position de MO pure est exclue des totaux en gardant son unit_price et sa
   * marge intacts.
   */
  setWorkforceDisabled(disabled: boolean) {
    if (this.unit_type === OfferUnitType.PACK) {
      this.shiftPackWorkforceShare(disabled);
      return;
    }
    if (this.unit_type === OfferUnitType.WORKFORCE) {
      this.is_workforce_disabled = disabled;
    }
  }

  /**
   * Retranche (ou remet) la part de main d'œuvre du prix STOCKÉ du pack, au lieu de
   * recomposer le pack depuis ses sous-lignes comme le fait recalculatePackTotals().
   *
   * Un pack dont le prix a été ajusté à la main diverge de la somme de ses sous-lignes
   * (forfait arrondi à 3'200.00 alors que les sous-lignes cumulent 3'213.70) : le
   * recomposer écraserait ce prix négocié, et un aller-retour désactiver/réactiver ne
   * rendrait pas le prix d'origine. Retrancher puis rajouter les mêmes montants rend cet
   * aller-retour exact par construction.
   *
   * Le delta ne porte que sur les sous-lignes qui changent réellement d'état : couper deux
   * fois de suite ne retranche donc la MO qu'une seule fois.
   */
  private shiftPackWorkforceShare(disabled: boolean) {
    if (!Array.isArray(this.articles)) this.articles = [];

    let costShare = 0;
    let sellingShare = 0;
    for (const article of this.articles) {
      if (article.article_type !== OfferUnitType.WORKFORCE) continue;
      if (!!article.is_workforce_disabled === disabled) continue;
      article.is_workforce_disabled = disabled;
      costShare += roundFixed(toNumber(article.cost_price) * toNumber(article.quantity), 2);
      sellingShare += roundFixed(toNumber(article.selling_price), 2);
    }
    if (costShare === 0 && sellingShare === 0) return;

    const sign = disabled ? -1 : 1;
    const normalizedUnitQuantity = toNumber(this.unit_quantity);
    const unitQuantity = normalizedUnitQuantity > 0 ? normalizedUnitQuantity : 1;

    // unit_price et margin sont les seuls champs de prix d'un pack réellement persistés :
    // le PV unitaire s'en dérive plutôt que d'être lu dans selling_price_unit, qui peut
    // ne pas encore avoir été calculé sur une ligne fraîchement désérialisée.
    const unitCostBefore = toNumber(this.unit_price);
    const unitSellingBefore = roundFixed(unitCostBefore * (1 + toNumber(this.margin) / 100), 2);

    const unitCost = roundFixed(unitCostBefore + sign * costShare, 6);
    const unitSelling = roundFixed(unitSellingBefore + sign * sellingShare, 2);

    this.unit_price = unitCost;
    this.selling_price_unit = unitSelling;
    this.cost_price = roundFixed(unitCost * unitQuantity, 2);
    this.selling_price = roundFixed(unitSelling * unitQuantity, 2);
    this.margin = unitCost !== 0 ? roundFixed(((unitSelling - unitCost) / unitCost) * 100, 12) : 0;

    // Même garde que recalculatePackTotals() : sans "selling_price_unit", computeValues()
    // écrase le PV unitaire par selling_price / unit_quantity.
    this.computeValues("selling_price_unit");
  }

  /**
   * Recompose le prix d'un pack depuis ses sous-lignes actives, pour le flux d'édition de la
   * composition d'un pack. Les sous-lignes sont la source de vérité du prix dans ce flux.
   *
   * L'enchaînement des trois setters et leur ordre reproduisent à l'identique le code qui
   * vivait avant dans le composant de chiffrage : ce flux est antérieur à la MO désactivable
   * et ne doit pas changer d'un centime. Écrire les champs directement depuis les cumuls
   * paraît plus lisible mais dérive la marge des cumuls unitaires au lieu des totaux arrondis,
   * ce qui décale le PV d'un centime sur un pack de quantité fractionnaire, et laisse
   * `computeSellingPrice()` reconstituer un prix de revient à partir du PV quand le coût des
   * sous-lignes est nul. Seul ajout ici : le saut des sous-lignes de MO désactivée.
   *
   * Le toggle de MO ne passe PAS par cette méthode mais par shiftPackWorkforceShare(), qui
   * préserve le prix du pack au lieu de le recomposer.
   */
  recalculatePackTotals() {
    if (this.unit_type !== OfferUnitType.PACK) return;
    if (!Array.isArray(this.articles)) this.articles = [];

    let cumulCostPrice = 0;
    let cumulSellingPrice = 0;
    for (const article of this.articles) {
      if (article.is_workforce_disabled) continue;
      const quantity = toNumber(article?.quantity);
      const costPrice = toNumber(article?.cost_price);
      const sellingPrice = toNumber(article?.selling_price);

      cumulCostPrice += roundFixed(costPrice * quantity, 2);
      cumulSellingPrice += roundFixed(sellingPrice, 2);
    }

    const normalizedUnitQuantity = toNumber(this.unit_quantity);
    const unitQuantity = normalizedUnitQuantity > 0 ? normalizedUnitQuantity : 1;

    this.setSellingPrice(cumulSellingPrice);
    this.setSellingPriceUnitInQuotation(cumulSellingPrice);
    this.setCostPrice(cumulCostPrice * unitQuantity);
  }

  get shouldCount(): boolean {
    return (
      !this.is_optional &&
      !this.isWorkforceLineDisabled &&
      this.offer_item_type !== OfferItemType.TEXT &&
      this.unit_type !== OfferUnitType.TEXT
    );
  }
  get is_variant(): boolean {
    return this.offer_item_type === OfferItemType.VARIANT;
  }
  get is_text(): boolean {
    return this.offer_item_type === OfferItemType.TEXT || this.unit_type === OfferUnitType.TEXT;
  }
  get is_percent(): boolean {
    return this.offer_item_type === OfferItemType.PERCENT;
  }

  get benefits() {
    return roundFixed(this.selling_price - this.cost_price, 2);
  }

  variants: OfferItemVariant[];

  articles: OfferItemArticle[];

  constructor(
    name = "",
    supplier_id = "",
    unit_type = OfferUnitType.MATERIAL,
    unit = "pce",
    unit_quantity = 1,
    unit_price = 0,
    margin = 25,
    auction_discount = 0,
    supplier_discount_goal = 25,
    progress = 0,
  ) {
    this.offer_item_id = generateObjectId();
    this.desc_html = "";
    this.name = name;
    this.offer_item_type = OfferItemType.NORMAL;
    this.supplier_id = supplier_id;
    this.unit_type = unit_type;
    this.unit = unit;
    this.unit_quantity = unit_quantity;
    this.unit_price = unit_price;
    this.margin = margin;
    this.auction_discount = auction_discount;
    this.supplier_discount_goal = supplier_discount_goal;
    this.progress = progress;

    this.billing_percent_situations = [];

    this.offer_discounts = [];

    this.variants = [];
    this.articles = [];

    // Non-enumerable : exclu de JSON.stringify(offer) donc jamais envoyé au backend
    // lors de la sauvegarde de l'offre (this.offerService.editQuotation(this.offer)).
    Object.defineProperty(this, "_discountInputMode", {
      value: "value",
      writable: true,
      enumerable: false,
      configurable: true,
    });
    Object.defineProperty(this, "_lastDiscountBaseSign", {
      value: null,
      writable: true,
      enumerable: false,
      configurable: true,
    });
  }

  public static createInstance(raw_offer_item: IOfferItem, offerDiscounts: OfferDiscount[] = []): OfferItem {
    const i = new OfferItem();
    i.offer_item_id = raw_offer_item.offer_item_id;
    i.name = raw_offer_item.name;
    i.article_id = raw_offer_item.article_id;
    i.article_number = raw_offer_item.article_number;
    i.isPageBreakBefore = raw_offer_item.isPageBreakBefore;
    i.isSellingPriceLocked = raw_offer_item.isSellingPriceLocked;
    i.isInvalid = raw_offer_item.isInvalid;
    i.isCostPriceLocked = raw_offer_item.isCostPriceLocked;
    i.offer_item_type = raw_offer_item.offer_item_type ? raw_offer_item.offer_item_type : OfferItemType.NORMAL;
    i.desc_html = raw_offer_item.desc_html ? raw_offer_item.desc_html : raw_offer_item.name;
    i.supplier_id = raw_offer_item.supplier_id;
    i.unit_quantity = roundFixed(raw_offer_item.unit_quantity, 6);
    i.percentage = roundFixed(raw_offer_item.percentage, 6);
    i.unit = raw_offer_item.unit;
    i.unit_type = OfferUnitType[raw_offer_item.unit_type];
    i.unit_price = roundFixed(raw_offer_item.unit_price, 6);
    i.progress = raw_offer_item.progress;
    i.margin = raw_offer_item.margin;
    i.auction_discount = raw_offer_item.auction_discount;
    i.supplier_discount_goal = raw_offer_item.supplier_discount_goal;
    i.offer_discounts = offerDiscounts;
    i.is_ttc = raw_offer_item.is_ttc;
    i.taxes_rate_percent = raw_offer_item.taxes_rate_percent;
    i.apply_discount = raw_offer_item.apply_discount;
    i.discount_value = raw_offer_item.discount_value;
    i.is_optional = raw_offer_item.is_optional;
    i.is_workforce_disabled = raw_offer_item.is_workforce_disabled ?? false;
    i.service_nature_id = raw_offer_item.service_nature_id || "";
    i.variants = OfferItemVariant.createInstances(raw_offer_item.variants);
    i.articles = OfferItemArticle.createInstances(raw_offer_item.articles);

    if (!raw_offer_item.billing_percent_situations || raw_offer_item.billing_percent_situations.length == 0) {
      i.billing_percent_situations = [];
    } else {
      i.billing_percent_situations = raw_offer_item.billing_percent_situations;
    }
    if (!raw_offer_item.employees_ids || raw_offer_item.employees_ids.length == 0) {
      i.employees_ids = [];
    } else {
      i.employees_ids = raw_offer_item.employees_ids;
    }
    i.computeValues();
    return i;
  }

  public static createInstances(jsonData: any[], offerDiscounts = []): OfferItem[] {
    return jsonData.map((val) => {
      return OfferItem.createInstance(val, offerDiscounts);
    });
  }
  replaceIds() {
    this.offer_item_id = generateObjectId();
  }
  computeValues(currentSetter = "") {
    if (this.is_variant) {
      this.computeVariantValues();
    } else {
      if (currentSetter !== "selling_price") this.computeSellingPrice();
      if (currentSetter !== "selling_price_unit") this.computeSellingPriceUnit();
      if (currentSetter !== "cost_price") this.computeCostPrice();
      if (currentSetter !== "selling_profit") this.computeSellingProfit();

      if (currentSetter !== "selling_price_with_auction") this.computeSellingPriceWithAuction();
      if (currentSetter !== "selling_auction_rate") this.computeSellingAuctionRate();
      if (currentSetter !== "selling_auction") this.computeSellingAuction();

      if (currentSetter !== "supplier_discount_goal_total") this.computeSupplierDiscountGoalTotal();
      if (currentSetter !== "buy_price_goal") this.computeBuyPriceGoal();
      if (currentSetter !== "supplier_discount_goal_rate") this.computeSupplierDiscountGoalRate();

      if (currentSetter !== "discount_percent") this.computeDiscountPercent();
      // if (currentSetter !== "discount_value") this.computeDiscountValue();
    }
  }

  computeVariantValues() {
    this.selling_price = this.variants.reduce((tot, item) => tot + item.selling_price, 0);
    this.cost_price = this.variants.reduce((tot, item) => tot + item.cost_price, 0);
    this.unit_quantity = this.variants.reduce((tot, item) => tot + item.quantity, 0);
    this.selling_profit = this.variants.reduce((tot, item) => tot + item.selling_price - item.cost_price, 0);

    this.selling_price_with_auction = this.selling_price;
    if (this.offer_discounts) {
      this.offer_discounts.forEach((offer_discount) => {
        this.selling_price_with_auction *= 1 - offer_discount.discount / 100;
      });
    }

    this.selling_auction = this.selling_price - this.selling_price_with_auction;
    this.supplier_discount_goal_total = (this.selling_price_with_auction * this.supplier_discount_goal) / 100;
    this.buy_price_goal = this.selling_price_with_auction - this.supplier_discount_goal_total || 0;
  }
  // computed values

  // SELLING PRICE
  public selling_profit: number = 0;
  computeSellingProfit() {
    this.selling_profit = roundFixed((+this.margin / 100) * +this.cost_price, 2);
  }
  public selling_price: number = 0;
  setSellingPrice(new_selling_price: number, recalculate = true) {
    this.selling_price = roundFixed(new_selling_price, 2);
    if (recalculate) {
      // this.setSellingPriceUnit(this.selling_price / (this.unit_quantity != 0 ? this.unit_quantity : 1), false);
      if (this.cost_price !== 0) {
        this.setMargin(((this.selling_price - this.cost_price) / this.cost_price) * 100, false);
      }
      this.computeValues("selling_price");
    }
  }

  computeSellingPrice() {
    if (this.unit_price === 0 && this.cost_price === 0 && this.selling_price_unit !== 0 && this.unit_quantity !== 0) {
      this.unit_price =
        this.margin === 0
          ? this.selling_price_unit
          : this.selling_price_unit / (1 + this.margin / 100) / this.unit_quantity;
    }
    this.selling_price = roundFixed(+this.unit_price * this.unit_quantity * (this.margin / 100 + 1), 2);
  }

  public selling_price_unit: number = 0;
  setSellingPriceUnit(new_selling_price: number, recalculate = true) {
    this.selling_price_unit = roundFixed(new_selling_price, 2);
    //this.unit_price = this.selling_price_unit / (1 + this.margin / 100);

    if (recalculate) {
      this.setMargin(((this.selling_price_unit - this.unit_price) / this.unit_price) * 100, false);

      this.computeValues("selling_price_unit");
    }
  }
  setSellingPriceUnitInQuotation(new_selling_price: number, recalculate = true) {
    this.selling_price_unit = roundFixed(new_selling_price, 2);
    this.unit_price = this.selling_price_unit / (1 + this.margin / 100);

    if (recalculate) {
      this.setMargin(((this.selling_price_unit - this.unit_price) / this.unit_price) * 100, false);

      this.computeValues("selling_price_unit");
    }
  }
  setSellingPriceInQuotation(new_selling_price: number, recalculate = true) {
    this.selling_price = roundFixed(new_selling_price, 2);
    this.selling_price_unit = this.selling_price / this.unit_quantity;
    this.unit_price = this.selling_price_unit / (1 + this.margin / 100);

    if (recalculate) {
      this.setMargin(((this.selling_price_unit - this.unit_price) / this.unit_price) * 100, false);

      this.computeValues("selling_price");
    }
  }

  computeSellingPriceUnit() {
    if (this.unit_quantity === 0) {
      this.selling_price_unit = roundFixed(+this.selling_price, 2);
    } else {
      this.selling_price_unit = roundFixed(+this.selling_price / this.unit_quantity, 2);
    }
  }
  // COST PRICE
  public cost_price: number = 0;
  setCostPrice(new_cost_price: number) {
    this.cost_price = roundFixed(new_cost_price, 2);
    this.unit_price = roundFixed(this.cost_price / this.unit_quantity, 6);
    if (this.cost_price === 0) return;
    if (this.selling_price !== 0) {
      this.setMargin(((this.selling_price - this.cost_price) / this.cost_price) * 100, false);
    }
  }
  computeCostPrice() {
    if (this.unit_price === 0 && this.cost_price === 0 && this.selling_price !== 0) {
      this.unit_price =
        (this.margin === 0 ? this.selling_price : this.selling_price / (1 + this.margin / 100)) / this.unit_quantity;
    }
    this.cost_price = roundFixed(this.unit_price * this.unit_quantity, 2);
  }

  // SELLING DISCOUNT
  public selling_price_with_auction: number = 0;
  computeSellingPriceWithAuction() {
    let newPrice = this.sellingPriceWithDiscount;

    if (this.offer_discounts) {
      for (const offer_discount of this.offer_discounts) {
        let discount_total = (newPrice * offer_discount.discount) / 100;
        newPrice = newPrice - discount_total;
      }
    }
    this.selling_price_with_auction = newPrice;
  }

  public selling_auction_rate: number = 0;
  computeSellingAuctionRate() {
    this.selling_auction_rate = Math.abs((this.selling_price_with_auction / this.selling_price) * 100 - 100) || 0;
  }

  public selling_auction: number = 0;
  computeSellingAuction() {
    this.selling_auction = this.selling_price - this.selling_price_with_auction;
  }

  // BUYING GOAL
  public supplier_discount_goal_total: number = 0;
  computeSupplierDiscountGoalTotal() {
    this.supplier_discount_goal_total = (this.selling_price_with_auction * this.supplier_discount_goal) / 100;
  }
  public buy_price_goal: number = 0;
  computeBuyPriceGoal() {
    this.buy_price_goal = this.selling_price_with_auction - this.supplier_discount_goal_total || 0;
  }
  public supplier_discount_goal_rate: number = 0;
  computeSupplierDiscountGoalRate() {
    this.supplier_discount_goal_rate = Math.abs((this.selling_price_with_auction / this.cost_price) * 100 - 100) || 0;
  }

  // BILLING
  billing_amount(situationIndex) {
    return this.sellingPriceWithDiscount * (this.get_billing_percent_situations(situationIndex) / 100);
  }

  get_billing_percent_situations(situationIndex): number {
    return this.billing_percent_situations[situationIndex] ? this.billing_percent_situations[situationIndex] : 0;
  }

  set_billing_percent_situations(value, situationIndex) {
    if (situationIndex > this.billing_percent_situations.length) {
      for (let i = this.billing_percent_situations.length; i < situationIndex; i++) {
        this.billing_percent_situations.push(0);
      }
    }
    this.billing_percent_situations[situationIndex] = value;
  }

  computeDiscountPercent() {
    const currentSign = Math.sign(this.selling_price) || null;
    if (
      this._discountInputMode === "percent" &&
      this._lastDiscountBaseSign !== null &&
      currentSign !== null &&
      currentSign !== this._lastDiscountBaseSign
    ) {
      // Le prix de la ligne a changé de signe (ex: quantité passée en moins-value)
      // depuis la saisie du rabais en %. Le montant figé ne correspond plus au bon
      // signe : on le re-dérive du % (source de vérité en mode "percent").
      this.computeDiscountValue();
    }
    this._lastDiscountBaseSign = currentSign;

    this.discount_percent = roundFixed(Math.abs((this.discount_value / this.selling_price) * 100) || 0, 6);
    if (!isFinite(this.discount_percent)) {
      this.discount_percent = 0;
    }
  }
  computeDiscountValue() {
    this.discount_value = roundFixed((this.discount_percent * this.selling_price) / 100 || 0, 6);
  }

  // OFFER DISCOUNTS
  setOfferDiscounts(discounts: OfferDiscount[]) {
    this.offer_discounts = discounts;
    this.computeValues();
  }

  get sellingPriceTTC() {
    // Prix de vente avec toutes les taxes comprises (TTC)
    return this.selling_price * (!this.is_ttc ? this.taxes_rate : 1);
  }
  get sellingPriceWithDiscount() {
    // Prix de vente avec éventuelle réduction appliquée
    return this.selling_price - (this.apply_discount ? this.discount_value : 0);
  }
  get sellingPriceTTCWithDiscount() {
    // Prix de vente final, incluant à la fois les taxes et les réductions
    return this.sellingPriceWithDiscount * (!this.is_ttc ? this.taxes_rate : 1);
  }

  get cumulateArticlesSellingPriceForPack() {
    let cumul = 0;

    for (let a of this.articles) {
      cumul += round(a.selling_price, 2);
    }

    return cumul;
  }

  public static recalculatePercentGroup(percentGroups: OfferItem[][]) {
    for (let percentGroup of percentGroups) {
      let percentTypeItem: OfferItem = percentGroup.find((x) => x.offer_item_type === OfferItemType.PERCENT);

      let total = 0;
      let price = 0;
      for (let item of percentGroup.filter((x) => x.offer_item_type !== OfferItemType.PERCENT && x.shouldCount)) {
        total += item.sellingPriceWithDiscount;
      }
      price = total * (percentTypeItem.percentage / 100);

      percentTypeItem.setCostPrice(price);
      percentTypeItem.setSellingPrice(price);
    }

    // after this function you must recalculate the discounts
  }
}

function generateObjectId(): string {
  const timestamp = Math.floor(new Date().getTime() / 1000).toString(16);
  const randomHex = Array.from({ length: 16 }, () => Math.floor(Math.random() * 16).toString(16)).join("");

  return timestamp + randomHex;
}
export function roundFixed(num: number, decimalPlaces: number): number {
  const factor = Math.pow(10, decimalPlaces);
  return Math.round(num * factor) / factor;
}

/**
 * Réplique la coercition de `toNumericValue` du composant chiffrage, y compris la
 * décimale à virgule : les prix et quantités des sous-lignes viennent d'inputs et
 * peuvent arriver sous forme de chaîne "12,5". Un simple Number() renverrait NaN.
 */
export function toNumber(value: number | string | null | undefined): number {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : 0;
  }
  if (typeof value === "string") {
    const parsed = Number.parseFloat(value.replace(",", "."));
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
}
